import { createSlice } from "@reduxjs/toolkit";

// Safe load from localStorage
function loadCartItems() {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem("cartItems");
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to parse cartItems from localStorage", error);
      return [];
    }
  }
  return [];
}

const initialState = {
  cartItems: loadCartItems(),
  cartTotalQuantity: 0,
  cartTotalAmount: 0,
  discountAmount: 0,
  discountPercentage: 0,
};

export const CartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action) {
      const productIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.id
      );
      if (productIndex >= 0) {
        state.cartItems[productIndex].cartQuantity += 1;
      } else {
        const tempProduct = { ...action.payload, cartQuantity: 1 };
        state.cartItems.push(tempProduct);
      }
      if (typeof window !== "undefined") {
        localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
      }
    },

    removeFromCart(state, action) {
      state.cartItems = state.cartItems.filter(
        (cartItem) => cartItem.id !== action.payload.id
      );
      if (typeof window !== "undefined") {
        localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
      }
    },

    cleanCart(state) {
      state.cartItems = [];
      state.cartTotalQuantity = 0;
      state.cartTotalAmount = 0;
      state.discountAmount = 0;
      state.discountPercentage = 0;
      if (typeof window !== "undefined") {
        localStorage.removeItem("cartItems");
      }
    },

    decreaseCart(state, action) {
      const itemIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.id
      );
      if (state.cartItems[itemIndex].cartQuantity > 1) {
        state.cartItems[itemIndex].cartQuantity -= 1;
      }
      if (typeof window !== "undefined") {
        localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
      }
    },

    incrementCart(state, action) {
      const productIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.id
      );
      if (productIndex >= 0) {
        state.cartItems[productIndex].cartQuantity += 1;
      } else {
        const tempProduct = { ...action.payload, cartQuantity: 1 };
        state.cartItems.push(tempProduct);
      }
      state.discountAmount =
        (state.cartTotalAmount * state.discountPercentage) / 100;
      if (typeof window !== "undefined") {
        localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
      }
    },

    addByIncrement(state, action) {
      const productIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.product.id
      );
      if (productIndex >= 0) {
        state.cartItems[productIndex].cartQuantity += action.payload.cartQuantity;
      } else {
        const tempProduct = {
          ...action.payload.product,
          cartQuantity: action.payload.cartQuantity || 1,
        };
        state.cartItems.push(tempProduct);
      }
      if (typeof window !== "undefined") {
        localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
      }
    },

    getTotals(state) {
      const { total, quantity } = state.cartItems.reduce(
        (cartTotal, cartItem) => {
          const { currentprice = 0, cartQuantity = 0 } = cartItem;
          const itemTotal = currentprice * cartQuantity;

          cartTotal.total += itemTotal;
          cartTotal.quantity += cartQuantity;

          return cartTotal;
        },
        { total: 0, quantity: 0 }
      );

      state.cartTotalQuantity = quantity;

      const discount = state.discountAmount || 0;
      state.cartTotalAmount = Math.max(0, parseFloat((total - discount).toFixed(2)));
    },

    applyDiscount(state, action) {
      state.discountAmount = action.payload;
    },

    setDiscountPercentage(state, action) {
      state.discountPercentage = action.payload;
    },

    resetDiscount(state) {
      state.discountAmount = 0;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  cleanCart,
  decreaseCart,
  incrementCart,
  getTotals,
  addByIncrement,
  applyDiscount,
  resetDiscount,
  setDiscountPercentage,
} = CartSlice.actions;

export const cartReducer = CartSlice.reducer;
